import { add, formatDistanceStrict } from "date-fns";

import { hasActiveAge } from "#app/lib/age-status";
import {
  isClosedStageType,
  isUpdateStep,
  type IssueAgeRow,
  type IssueAgeStatus,
  type StageOption,
  type UpdateCycleRow,
  type UpdateStep,
  type WorkspaceWorkflow,
} from "#app/lib/data";

// ---------------------------------------------------------------------------
// The update cycle, client side
//
// The client mirror of `apps/api/core/src/stages/effective-cycle.ts`. One rule
// everywhere: Project off → off; else the Project's own cycle (which survives
// Stage moves) ?? the Stage's Admin pin (which counts on any Stage type) ?? the
// Workflow default — a rung unpinned CLOSED Stages never reach, because
// finished work doesn't nag unless an Admin pinned a cycle onto the closed
// Stage itself.
//
// Every input here is a projection of a row alias rather than a shape of its
// own (R2): the ladder reads three columns off a Stage and one off a Workflow,
// and no single fragment is the common ancestor of every caller's row — the
// board's stage, the issue view's stage and the settings row are four different
// fragments. Deriving with `Pick` keeps the drift protection an alias gives:
// drop `updateCycleOff` from the fragment and this file stops compiling.
// ---------------------------------------------------------------------------

/**
 * A cycle whose step has been narrowed to the closed set. `UpdateCycle.step` is
 * `String` on the wire — the schema publishes no enum for it — so
 * `normalizeUpdateCycle` is the ONE place that narrowing happens, and
 * everything downstream carries the narrow type.
 */
export type UpdateCycle = Omit<UpdateCycleRow, "__typename" | "step"> & {
  step: UpdateStep;
};

/** The Stage columns the ladder reads: its type, and the Admin's pin (the
 *  cycle pair plus the off flag). Every stage fragment spreads
 *  `StageOption_stage`, so any stage row in the app satisfies this. */
export type StagePin = Pick<
  StageOption,
  "type" | "updateCycle" | "updateCycleOff"
>;

/** The bottom rung: the Workflow's default cycle. Null = updates off for every
 *  unpinned Stage in the Workflow. */
export type WorkflowCycle = Pick<WorkspaceWorkflow, "updateCycle">;

/** The per-Project rungs — its own cycle and its own off switch. Absent both,
 *  the Project inherits.
 *
 *  The keys are OPTIONAL, not nullable: a row that never selected the cycle
 *  columns inherits, which is the truth, where a stand-in `updatesOff: false`
 *  would be a claim it has no basis for (the column is `Boolean!`). The `Pick`
 *  is what keeps the drift protection — drop `updatesOff` from the fragment and
 *  this file stops compiling. */
type ProjectCycleState = Partial<
  Pick<IssueAgeRow, "updatesOff" | "cycle">
>;

/** The final slice of a cycle reads as Update soon: On track until 85% of the
 *  window has elapsed, Update soon for the last 15%, Stale past the end. Mirrors
 *  `DUE_WINDOW_RATIO` in `stages/effective-cycle.ts` — change both together. */
const UPDATE_DUE_WINDOW_RATIO = 0.15;

/** The cycle pair, however it arrives: straight off a fragment or already
 *  narrowed. Everything that only reads amount+step takes this. */
export type StoredUpdateCycle = Pick<UpdateCycleRow, "amount" | "step">;

/** A stored cycle narrowed to the closed step set, or null when either half is
 *  missing or the amount isn't positive. A half-set cycle is off, not an
 *  error — the server stores the pair as two nullable columns. */
export function normalizeUpdateCycle(
  cycle: StoredUpdateCycle | null | undefined,
): UpdateCycle | null {
  if (!cycle) return null;
  const { amount, step } = cycle;
  if (!Number.isFinite(amount) || amount <= 0 || !isUpdateStep(step)) {
    return null;
  }
  return { amount, step };
}

// ---------------------------------------------------------------------------
// Effective cycle resolution
// ---------------------------------------------------------------------------

/** Which rung answered — `off` when none did. */
type UpdateCycleSource = "project" | "stage" | "workflow" | "off";

type EffectiveUpdateCycle = {
  cycle: UpdateCycle | null;
  source: UpdateCycleSource;
};

export {
  cycleKey,
  DEFAULT_FOLLOW_UP_CYCLE,
  findFollowUpCyclePreset,
  FOLLOW_UP_CYCLE_PRESETS,
  formatUpdateCycleShort,
  nearestFollowUpCyclePreset,
} from "#app/lib/follow-up-cycle";

/** The Workflow's default, normalized. */
function workflowDefaultCycle(
  workflow: WorkflowCycle | null | undefined,
): UpdateCycle | null {
  return normalizeUpdateCycle(workflow?.updateCycle);
}

export function effectiveStageCycle(
  stage: StagePin | null | undefined,
  workflow?: WorkflowCycle | null,
): EffectiveUpdateCycle {
  if (stage?.updateCycleOff) return { cycle: null, source: "off" };
  const pin = normalizeUpdateCycle(stage?.updateCycle);
  if (pin) return { cycle: pin, source: "stage" };
  // Unpinned closed Stages stop here — updates on finished work are the
  // Admin's per-Stage opt-in or nothing, never the inherited default.
  if (isClosedStageType(stage?.type)) return { cycle: null, source: "off" };
  const fallback = workflowDefaultCycle(workflow);
  return fallback
    ? { cycle: fallback, source: "workflow" }
    : { cycle: null, source: "off" };
}

export function effectiveProjectCycle(
  age: ProjectCycleState | null | undefined,
  stage: StagePin | null | undefined,
  workflow?: WorkflowCycle | null,
): EffectiveUpdateCycle {
  if (age?.updatesOff) return { cycle: null, source: "off" };
  const own = normalizeUpdateCycle(age?.cycle);
  if (own) return { cycle: own, source: "project" };
  return effectiveStageCycle(stage, workflow);
}

/**
 * THE definition of "this Project has a live clock", for every affordance that
 * would post against it: a status is stored AND the effective cycle resolves.
 * The stored column alone is not enough — a Workflow that turns updates off
 * leaves the statuses it had stamped in place, and a chip that trusted them
 * would offer an Update no dialog can take. A host that cannot resolve the
 * Stage (`stage === undefined`, the map still loading or never passed) falls
 * back to the stored status, the same fallback the live color takes.
 */
export function clockRunning(
  age: ProjectAge | null | undefined,
  stage: StagePin | null | undefined,
  workflow?: WorkflowCycle | null,
): boolean {
  if (!hasActiveAge(age)) return false;
  if (stage === undefined) return true;
  return effectiveProjectCycle(age, stage, workflow).cycle != null;
}

// ---------------------------------------------------------------------------
// Live status derivation
// ---------------------------------------------------------------------------

/** The age columns display surfaces carry (`IssueAgeParts`). Optional for the
 *  same reason `ProjectCycleState` is: a surface that carries only the clock
 *  (the legacy dashboard's activity-derived rows) reads its stored status
 *  through the same label and inherits the rest. */
export type ProjectAge = Partial<
  Pick<IssueAgeRow, "date" | "status" | "updatesOff" | "cycle">
>;

/** A cycle as a date-fns Duration — calendar-aware, so "1 month" lands on the
 *  same day next month rather than a flat 30 days. */
export function updateCycleDuration(
  cycle: UpdateCycle,
): Parameters<typeof add>[1] {
  return { [cycle.step]: cycle.amount };
}

/** When the next Update is due — the deadline the status thresholds hang off,
 *  and the date the "Next" row reads. Null when nothing is being measured. */
export function nextUpdateDue(
  age: ProjectAge | null | undefined,
  stage: StagePin | null | undefined,
  workflow?: WorkflowCycle | null,
): Date | null {
  if (!age?.date || age.updatesOff) return null;
  const { cycle } = effectiveProjectCycle(age, stage, workflow);
  if (!cycle) return null;
  const startedAt = new Date(age.date);
  if (Number.isNaN(startedAt.getTime())) return null;
  return add(startedAt, updateCycleDuration(cycle));
}

/**
 * The deadline as the "Next" line reads it: "in 3 days", "19 minutes
 * overdue", or "Not scheduled" when nothing is being measured. A single largest
 * unit, because the reader is deciding whether to act today — not scheduling
 * against it.
 *
 * "3 days overdue", the deadline vocabulary every dated thing shares.
 */
export function formatNextUpdate(
  deadline: Date | null,
  now: Date = new Date(),
): string {
  if (!deadline) return "Not scheduled";
  if (now >= deadline) {
    return `${formatDistanceStrict(now, deadline)} overdue`;
  }
  return `in ${formatDistanceStrict(deadline, now)}`;
}

/**
 * The deadline as a row's trailing fact reads it: "3 days overdue" once
 * passed, "Due in 3 days" ahead — `formatNextUpdate` with the subject named,
 * so the bare "in 3 days" never stands alone at the end of a row. The queue
 * rows, the clock's press tooltip and the follow up card all read this one
 * line.
 */
export function formatDueLine(deadline: Date, now: Date = new Date()): string {
  return deadline > now ? `Due ${formatNextUpdate(deadline, now)}` : formatNextUpdate(deadline, now);
}

/**
 * The status the viewer should SEE right now, derived from the last posted
 * Update and the effective cycle — the client mirror of `AgeStatusCron`'s
 * thresholds: On track until 85% of the cycle has elapsed, Update soon for the
 * final 15%, Stale past the end.
 *
 * The stored `age.status` column only moves when the cron pass fires, so
 * between passes — and in local dev, where nothing calls it — it lags reality
 * and a Project can sit green while reading "19 minutes overdue". DISPLAY
 * surfaces prefer this and fall back to the stored value on null (no Update
 * posted yet, updates off, or a caller that could not resolve the Stage).
 * Everything keyed to the stored column — filters, grouping, automations,
 * dashboard counts — stays on it, because those mirror server queries and have
 * to agree with them.
 *
 * Calendar-aware via `add`, like the deadline readout beside it, so the color
 * can never disagree with the text. The cron's fixed-ms months drift from
 * calendar months by a day or two at most, and its next pass reconciles the
 * stored value it owns.
 */
export function liveAgeStatus(
  age: ProjectAge | null | undefined,
  stage: StagePin | null | undefined,
  workflow?: WorkflowCycle | null,
  now: Date = new Date(),
): IssueAgeStatus | null {
  const deadline = nextUpdateDue(age, stage, workflow);
  if (!deadline || !age?.date) return null;
  const startedAt = new Date(age.date);
  const spanMs = deadline.getTime() - startedAt.getTime();
  if (spanMs <= 0) return null;
  const nowMs = now.getTime();
  if (nowMs >= deadline.getTime()) return "offTrack";
  if (nowMs >= deadline.getTime() - spanMs * UPDATE_DUE_WINDOW_RATIO) {
    return "atRisk";
  }
  return "onTrack";
}

// ---------------------------------------------------------------------------
// Drafts + formatting
// ---------------------------------------------------------------------------

/** A cycle as a cadence: "Every 2 weeks". Null is "Follow ups off" — the phrase
 *  every surface uses for a Project nothing measures. */
export function formatUpdateCycle(
  cycle: StoredUpdateCycle | null | undefined,
): string {
  const normalized = normalizeUpdateCycle(cycle);
  if (!normalized) return "Follow ups off";
  if (normalized.amount === 1) {
    return `Every ${normalized.step.replace(/s$/, "")}`;
  }
  return `Every ${normalized.amount} ${normalized.step}`;
}
